import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackAnalyticsEvent } from '@/lib/api';

const SESSION_STORAGE_KEY = 'sle_analytics_session_id';

const getSessionId = () => {
  if (typeof window === 'undefined') return '';
  try {
    let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
    }
    return sid;
  } catch (e) {
    return `sess_${Date.now()}`;
  }
};

const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

export const trackEvent = async (eventType, payload = {}) => {
  try {
    const sessionId = getSessionId();
    const deviceType = getDeviceType();
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';

    await trackAnalyticsEvent({
      event_type: eventType,
      path: payload.path || currentPath,
      search_query: payload.search_query || null,
      referrer,
      device_type: deviceType,
      session_id: sessionId,
      metadata: payload.metadata || {}
    });
  } catch (err) {
    // Non-blocking telemetry
    console.debug('Analytics event dropped:', err);
  }
};

export function AnalyticsTracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    const currentPath = location.pathname;
    // Exclude internal admin dashboard views from public visitor metrics
    if (currentPath.startsWith('/admin')) {
      return;
    }

    // Prevent duplicate triggers for the exact same path
    if (lastPathRef.current === currentPath) {
      return;
    }
    lastPathRef.current = currentPath;

    // Small delay to ensure document title and state are set
    const timer = setTimeout(() => {
      trackEvent('page_view', { path: currentPath });
    }, 150);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return null;
}

export default useAnalytics;
export function useAnalytics() {
  return {
    trackEvent,
    trackSearch: (query) => trackEvent('search_query', { search_query: query }),
    trackCatalogueDownload: () => trackEvent('catalogue_download', { path: '/catalogue/download' }),
    trackInquiry: (metadata = {}) => trackEvent('inquiry_submit', { metadata })
  };
}
